import type { OrderLocation } from "./InfoPopup";
import {
  OrderLocationDto,
  OptimizeRouteRequestDto,
  DriverForOptimizeDto,
  LatLng,
} from "./routeTypes";
import orderServices from "../../services/orders/orderservice";
import type { RouteReviewContext } from "./RoutePreviewDialog";
import { DriverAssignment, parseRoutePoint } from "./route-preview";

/* ───────────────── Types ───────────────── */

export type OrderLocationWithSeq = OrderLocation & {
  sequence?: number;
  amount?: number;
  taxableAmount?: number;
  totalDistanceKm?: number;
  drivingMinutes?: number;
};

export type OptimizedDriverRoute = {
  driverId: number;
  driverName: string;
  color?: string;
  phonenumber: string;
  start?: LatLng;
  end?: LatLng;
  stops: OrderLocationWithSeq[];
  totalDistanceKm?: number;
  drivingMinutes?: number;

  // NEW: per-driver totals computed by matching stop.orderId -> original locations
  totalAmount?: number;
  totalTaxableAmount?: number;

  // Include vehicle number for UI/Excel later
  vehicleNo?: string;
};

/* ───────────────── Helpers ───────────────── */

const idKey = (id: unknown) => String(id ?? "");

/* ───────────────────────────────────────────────────────────────────────────
 * groupStopsByLocation
 *
 * PROBLEM: The optimizer can interleave orders from the same GPS location.
 * e.g. Person A has 5 orders at L1, Person B has 2 at L2, Person C has 3 at L3.
 * The optimizer might return: L1(1 order) → L2(1 order) → L1(1 order) → …
 * This forces the driver to revisit the same address multiple times.
 *
 * SOLUTION (v3 — phone + GPS grouping):
 * Grouping key = phone number  +  GPS rounded to 3 decimal places (~111 m).
 *
 * Rules:
 *  • Same phone + same address (~111 m)  → ONE group   → deliver all before moving on
 *  • Same phone + different address       → TWO groups  → the optimizer decides visit order
 *  • No phone available                   → GPS-only key (4 dp ≈ 11 m)
 *
 * Why 3 dp (~111 m) for address grouping?
 *  - Handles GPS drift between orders at the same physical address
 *  - Large enough to catch "same building" variations
 *  - Small enough that distinct addresses in the same neighbourhood are NOT merged
 * ─────────────────────────────────────────────────────────────────────────── */
function groupStopsByLocation(
  stops: OrderLocationWithSeq[],
  phoneByOrderId: Map<string, string>
): OrderLocationWithSeq[] {
  if (stops.length === 0) return stops;

  /**
   * Grouping key:
   *  - phone present  → "ph:<phone>:gps:<lat3dp>,<lng3dp>"
   *                     (same customer at same address = one stop;
   *                      same customer at different address = different stop)
   *  - no phone       → "gps:<lat4dp>,<lng4dp>"  (GPS-only fallback, ~11 m)
   */
  const stopKey = (s: OrderLocationWithSeq): string => {
    const phone = phoneByOrderId.get(idKey(s.orderId))?.trim();
    if (phone) {
      // 3 decimal places ≈ 111 m  — groups same address, separates different addresses
      return `ph:${phone}:gps:${s.lat.toFixed(3)},${s.lng.toFixed(3)}`;
    }
    return `gps:${s.lat.toFixed(4)},${s.lng.toFixed(4)}`;
  };

  // Sort by the optimizer's sequence so Map insertion order = visit order
  const sorted = [...stops].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

  // Build an ordered map: groupKey → stops[]
  const groupMap = new Map<string, OrderLocationWithSeq[]>();
  for (const stop of sorted) {
    const key = stopKey(stop);
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push(stop);
  }

  // Flatten groups in visit order and assign consecutive sequences 1, 2, 3, …
  const result: OrderLocationWithSeq[] = [];
  let seq = 1;
  for (const items of groupMap.values()) {
    for (const item of items) {
      result.push({ ...item, sequence: seq++ });
    }
  }

  return result;
}


/* ───────────────── Main ───────────────── */

export async function optimizeRoutesForDrivers(
  locations: OrderLocation[], selected: DriverAssignment[]
): Promise<OptimizedDriverRoute[]> {
  if (!locations.length || !selected.length) throw new Error("Include at least one order and executive.");

  // Build quick lookups by orderId (from original selection)
  const amountById = new Map<string, number>();
  const taxableById = new Map<string, number>();
  // Phone lookup — used by groupStopsByLocation as primary grouping key
  const phoneByOrderId = new Map<string, string>();
  for (const loc of locations) {
    const key = idKey(loc.orderId);
    if (!key) continue;
    amountById.set(key, Number((loc as any).amount ?? 0));
    taxableById.set(key, Number((loc as any).taxableAmount ?? 0));
    const phone = (loc as any).primaryContactNbr ?? "";
    if (phone) phoneByOrderId.set(key, String(phone));
  }

  /* ───── Build drivers[] for optimize API ───── */
  let drivers: DriverForOptimizeDto[];
  try {
    drivers = selected.map(({ driver }) => ({
      driverId: driver.driverId,
      desiriUserId: driver.desiriUserId,
      name: driver.name,
      phonenumber: driver.phonenumber,
      color: driver.color,
      start: parseRoutePoint(driver.startLocation),
      end: parseRoutePoint(driver.endLocation),
    }));
  } catch (e) {
    throw new Error("Check the executive's start and end coordinates.");
  }

  const dtoLocations: OrderLocationDto[] = locations.map((loc) => ({
    lat: loc.lat,
    lng: loc.lng,
    orderId: loc.orderId,
    customerName: loc.customerName,
    amount: (loc as any).amount,                  // keep original amount if present
    taxableAmount: (loc as any).taxableAmount ?? 0,
    sequence: (loc as OrderLocationWithSeq).sequence ?? 0,
    totalDistanceKm: (loc as any).totalDistanceKm ?? 0,
  }));

  const payload: OptimizeRouteRequestDto = { drivers, locations: dtoLocations };

  try {

    const resp = await orderServices.orderoptimized(payload);

    const routesRaw = Array.isArray(resp?.routes) ? resp.routes : [];

    const routes: OptimizedDriverRoute[] = routesRaw.map((r: any) => {
      const stopsRaw = Array.isArray(r.stops) ? r.stops : [];

      // Enrich stops with amounts + phone from our per-orderId lookups.
      // primaryContactNbr is needed by groupStopsByLocation (phone-first keying).
      const stopsEnriched: OrderLocationWithSeq[] = stopsRaw.map((s: any) => {
        const key = idKey(s.orderId);
        const amount = amountById.get(key) ?? Number(s.amount ?? 0);
        const taxableAmount = taxableById.get(key) ?? Number(s.taxableAmount ?? 0);
        const primaryContactNbr = phoneByOrderId.get(key) ?? s.primaryContactNbr ?? null;
        const point = parseRoutePoint(`${s.lat},${s.lng}`);

        return {
          lat: point.lat,
          lng: point.lng,
          orderId: s.orderId,
          customerName: s.customerName,
          primaryContactNbr,          // ← needed for phone-first grouping
          sequence: Number(s.sequence ?? 0),
          totalDistanceKm: Number(s.totalDistanceKm ?? 0),
          requestedBy: null,          // satisfies OrderLocation type
          amount,
          taxableAmount,
        };
      });

      // ── KEY FIX (v2 — phone-first grouping) ─────────────────────────────
      // Groups all orders sharing the same customer phone number (primary key)
      // or the same GPS coordinates (fallback). All orders for one customer are
      // delivered together before the driver moves to the next location.
      const stops: OrderLocationWithSeq[] = groupStopsByLocation(stopsEnriched, phoneByOrderId);

      // Per-driver totals
      const totalAmount = stops.reduce((sum, s) => sum + Number(s.amount ?? 0), 0);
      const totalTaxableAmount = stops.reduce((sum, s) => sum + Number(s.taxableAmount ?? 0), 0);
      const selectedItem = selected.find((s) => s.driver.driverId === Number(r.driverId));

      const route: OptimizedDriverRoute = {
        driverId: Number(r.driverId),
        driverName: selectedItem?.driver.name ?? r.driverName,
        color: r.color ?? r.Color ?? undefined,
        phonenumber: selectedItem?.driver.phonenumber ?? r.phonenumber ?? r.Phonenumber ?? "",
        start: r.start
          ? parseRoutePoint(`${r.start.lat ?? r.start.Lat},${r.start.lng ?? r.start.Lng}`)
          : parseRoutePoint(selectedItem?.driver.startLocation),
        end: r.end
          ? parseRoutePoint(`${r.end.lat ?? r.end.Lat},${r.end.lng ?? r.end.Lng}`)
          : parseRoutePoint(selectedItem?.driver.endLocation),
        stops,
        totalDistanceKm: Number(r.totalDistanceKm ?? 0),
        totalAmount,
        totalTaxableAmount,
        vehicleNo: selectedItem?.vehicleNo ?? "",
      };

      // Console summary per driver
      console.log(
        `Driver ${route.driverName}: Orders=${stops.length}, ` +
        `Distance=${(route.totalDistanceKm ?? 0).toFixed(2)} km, ` +
        `Total Amount=₹${totalAmount.toFixed(2)} (Taxable=₹${totalTaxableAmount.toFixed(2)})`
      );

      return route;
    });

    const expected = new Set(locations.map(o => String(o.orderId)));
    const actual = routes.flatMap(r => r.stops.map(o => String(o.orderId)));
    if (!routes.length || actual.length !== expected.size || new Set(actual).size !== actual.length ||
        actual.some(id => !expected.has(id)) || routes.some(r => !selected.some(s => s.driver.driverId === r.driverId)))
      throw new Error("The optimizer did not return every included order exactly once. Please retry.");
    return routes.filter(r => r.stops.length > 0);
  } catch (error) {
    throw error instanceof Error ? error : new Error("Could not optimize this route. Please retry.");
  }
}

export async function getOptimizedRoutesForDrivers(locations: OrderLocation[], assignments: DriverAssignment[],
  review: (context: RouteReviewContext) => Promise<OptimizedDriverRoute[] | null>): Promise<OptimizedDriverRoute[] | null> {
  if (!assignments.length || assignments.some(a => !a.vehicleNo) ||
    new Set(assignments.map(a => a.vehicleNo)).size !== assignments.length) return null;
  return review({ orders: locations, assignments, optimize: optimizeRoutesForDrivers });
}

/* ───────────────── Optional helper ───────────────── */

export function getTotalsForDriver(
  routes: OptimizedDriverRoute[] | null | undefined,
  driverId: number
) {
  const r = routes?.find((x) => x.driverId === driverId);
  return {
    totalAmount: r?.totalAmount ?? 0,
    totalTaxableAmount: r?.totalTaxableAmount ?? 0,
    orderCount: r?.stops?.length ?? 0,
    distanceKm: r?.totalDistanceKm ?? 0,
  };
}
